import { useEffect, useRef, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { notify } from '@/core/toast/notify';
import { recordAllowedPath, resolveSafeRevertPath } from '@/utils/routeAccess';

type RouteAccessGateProps = {
  denied: boolean;
  message?: string;
  fallbackPath: string;
  /** When true, redirect without showing an error toast. */
  silent?: boolean;
  canAccessPath?: (pathname: string) => boolean;
  extraRevertCandidates?: string[];
  children?: ReactNode;
};

/** Blocks outlet, shows a toast, and restores a previous allowed URL when possible. */
export function RouteAccessGate({
  denied,
  message,
  fallbackPath,
  silent = false,
  canAccessPath,
  extraRevertCandidates,
  children,
}: RouteAccessGateProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const notifiedRef = useRef(false);

  useEffect(() => {
    if (!denied) {
      notifiedRef.current = false;
      recordAllowedPath(location.pathname, location.search);
      return;
    }

    const revertTo = resolveSafeRevertPath(location.pathname, fallbackPath, {
      canAccess: canAccessPath,
      extraCandidates: extraRevertCandidates,
    });
    const current = `${location.pathname}${location.search}`;

    if (!notifiedRef.current) {
      notifiedRef.current = true;
      if (message && !silent) notify.error(message);
    }

    if (revertTo && revertTo !== current) {
      navigate(revertTo, { replace: true });
    }
  }, [
    denied,
    silent,
    message,
    fallbackPath,
    canAccessPath,
    extraRevertCandidates,
    location.pathname,
    location.search,
    navigate,
  ]);

  if (denied) return null;
  return children ?? <Outlet />;
}
